import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import SandboxBadge from "@/components/SandboxBadge";
import Layout from "@/components/Layout";
import CedenteLayout from "@/components/CedenteLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Operations from "@/pages/Operations";
import NewOperation from "@/pages/NewOperation";
import Clients from "@/pages/Clients";
import Users from "@/pages/Users";
import Profile from "@/pages/Profile";
import Commissions from "@/pages/Commissions";
import Policy from "@/pages/Policy";
import TokensAPI from "@/pages/TokensAPI";
import Register from "@/pages/Register";
import RegisterStatus from "@/pages/RegisterStatus";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Balances from "@/pages/Balances";
import Payments from "@/pages/Payments";
import Receivables from "@/pages/Receivables";
import Payers from "@/pages/Payers";
import Stock from "@/pages/Stock";
import APagar from "@/pages/APagar";
import CedenteOperations from "@/pages/cedente/Operations";
import CedenteClientes from "@/pages/cedente/Clientes";
import CedenteUsers from "@/pages/cedente/Users";
import CedenteSettings from "@/pages/cedente/Settings";
import { PORTAL_MODE, landingForUser, loginUrl, partnerSubdomainUrl, cedentePath } from "@/lib/portal";
import { isCedenteNewOperationEnabled } from "@/lib/cedenteOperations";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) {
    if (PORTAL_MODE === "partner") {
      window.location.href = loginUrl();
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  if (PORTAL_MODE === "cedente" && user.portal_type === "partner") {
    const url = partnerSubdomainUrl("/dashboard");
    if (url) { window.location.href = url; return null; }
  }
  const dest = landingForUser(user);
  if (!dest) {
    const url = partnerSubdomainUrl("/dashboard");
    if (url) { window.location.href = url; return null; }
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={dest} replace />;
}

/**
 * Shared route fragments — called as functions, NOT rendered as components.
 * React Router v7 only accepts <Route> and <Fragment> inside <Routes>.
 */
function partnerRoutes() {
  return (
    <>
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/operacoes" element={<Operations />} />
      <Route path="/operacoes/nova" element={<NewOperation />} />
      <Route path="/saldos" element={<Balances />} />
      <Route path="/pagamentos" element={<Payments />} />
      <Route path="/recebiveis" element={<Receivables />} />
      <Route path="/estoque" element={<Stock />} />
      <Route path="/a-pagar" element={<APagar />} />
      <Route path="/sacados" element={<Payers />} />
      <Route path="/clientes" element={<Clients />} />
      <Route path="/comissionamento" element={<Commissions />} />
      <Route
        path="/integracao/policy"
        element={
          <ProtectedRoute portalType="partner" roles={["admin"]}>
            <Policy />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integracao/tokens"
        element={
          <ProtectedRoute portalType="partner" roles={["admin"]}>
            <TokensAPI />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute portalType="partner" roles={["admin", "operator"]}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route path="/perfil" element={<Profile />} />
    </>
  );
}

function cedenteRoutes(prefix = "") {
  return (
    <>
      {prefix && <Route path={prefix} element={<Navigate to={`${prefix}/operacoes`} replace />} />}
      <Route path={`${prefix}/operacoes`} element={<CedenteOperations />} />
      {/* GATE DE ENTRADA (decisão 04(a) do owner): a rota só EXISTE com a flag
          de build `REACT_APP_CEDENTE_NEW_OPERATION` ligada. Flag ausente ⇒ o
          deep-link cai no catch-all (RootRedirect), como qualquer URL inválida.
          Papéis: só editor do cedente (viewer não cria operação). */}
      {isCedenteNewOperationEnabled() && (
        <Route
          path={`${prefix}/operacoes/nova`}
          element={
            <ProtectedRoute portalType="cedente" roles={["cedente_admin", "cedente_operator"]}>
              <NewOperation mode="cedente" />
            </ProtectedRoute>
          }
        />
      )}
      <Route path={`${prefix}/clientes`} element={<CedenteClientes />} />
      <Route path={`${prefix}/usuarios`} element={<CedenteUsers />} />
      <Route path={`${prefix}/configuracoes`} element={<CedenteSettings />} />
    </>
  );
}

/**
 * Shared public routes (login, register, password reset).
 */
function publicRoutes() {
  return (
    <>
      <Route path="/cadastro" element={<Register />} />
      <Route path="/cadastro/status/:userId" element={<RegisterStatus />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/redefinir-senha" element={<ResetPassword />} />
    </>
  );
}

function CedenteOnlyApp() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {publicRoutes()}
      <Route
        element={
          <ProtectedRoute portalType="cedente">
            <CedenteLayout />
          </ProtectedRoute>
        }
      >
        {cedenteRoutes()}
      </Route>
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function LoginRedirect() {
  window.location.href = loginUrl();
  return null;
}

function PartnerOnlyApp() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRedirect />} />
      {publicRoutes()}
      <Route
        element={
          <ProtectedRoute portalType="partner">
            <Layout />
          </ProtectedRoute>
        }
      >
        {partnerRoutes()}
      </Route>
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function MultiPortalApp() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {publicRoutes()}

      <Route
        element={
          <ProtectedRoute portalType="partner">
            <Layout />
          </ProtectedRoute>
        }
      >
        {partnerRoutes()}
      </Route>

      <Route
        element={
          <ProtectedRoute portalType="cedente">
            <CedenteLayout />
          </ProtectedRoute>
        }
      >
        {cedenteRoutes("/cedente")}
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  let tree;
  if (PORTAL_MODE === "cedente") tree = <CedenteOnlyApp />;
  else if (PORTAL_MODE === "partner") tree = <PartnerOnlyApp />;
  else tree = <MultiPortalApp />;

  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>{tree}</ErrorBoundary>
        <Toaster richColors position="top-right" />
        <SandboxBadge />
      </AuthProvider>
    </BrowserRouter>
  );
}
